import DeleteIcon from '@mui/icons-material/Delete';
import { Button, SelectChangeEvent, Tooltip } from '@mui/material';
import { ETranscoderProfileType, TranscoderProfile, TranscoderVideoProfile } from '@vxui/common';
import { TFunction } from 'i18next';
import { TableColumn } from 'react-data-table-component';
import { unsignedInteger } from '../../../../utils';
import { positiveNumber } from '../../../../utils/rules/positiveNumber';
import {
  // FpsOptions,
  Profiles,
  Resolutions,
  VideoCodecs,
} from '../../../profiles/forms/profileForm.helpers';
import { VideoProfileOnSelectProps } from '../../../profiles/forms/profileForm.types';
import { CellInput, CellSelectBox, ColumnName } from '../../../profiles/tables/profilesTable.helpers';
import { ProfileType } from '../templateForm.helpers';

type GetColumnsProps = {
  t: TFunction;
  actions: TableColumn<TranscoderVideoProfile>[];
  onChangeHandler: (row: TranscoderProfile) => void;
  onResolutionChangeHandler: (event: SelectChangeEvent, row: TranscoderProfile) => void;
  onCodecChangeHandler: (event: SelectChangeEvent, row: TranscoderProfile) => void;
  onSelectChangeHandler: ({ event, row, field, type }: VideoProfileOnSelectProps) => void;
};

export const defaultGopSize = 32;
export const defaultFPS = '25';

export const newVideoProfile = {
  id: '-1',
  type: ETranscoderProfileType.new,
  fps: defaultFPS,
  codec: 'h264',
  profile: 'main',
  gopSize: defaultGopSize,
} as TranscoderVideoProfile;

export const getColumns = ({
  t,
  actions,
  onChangeHandler,
  onResolutionChangeHandler,
  onCodecChangeHandler,
  onSelectChangeHandler,
}: GetColumnsProps): TableColumn<TranscoderVideoProfile>[] => {
  return [
    {
      cell: row => <ProfileType t={t} profile={row} />,
      width: '90px',
    },
    {
      name: ColumnName(t, 'name'),
      selector: row => row.name,
      cell: row => (
        <CellInput
          name="name"
          defaultValue={row.name ?? ''}
          error={!!row.errorMsg?.name}
          helperText={row.errorMsg?.name}
          placeholder={t('transcoder.forms.profile.video.fields.name.placeholder')}
          disabled={row.type === ETranscoderProfileType.shared}
          row={row}
          onChangeCellHandler={onChangeHandler}
        />
      ),
      sortable: true,
      minWidth: '230px',
    },
    {
      name: ColumnName(t, 'codec'),
      selector: row => row.codec,
      cell: row => (
        <CellSelectBox
          fieldName="codec"
          row={row}
          value={row?.codec?.toString() ?? ''}
          items={VideoCodecs}
          placeholder={t('transcoder.forms.profile.video.fields.codec.placeholder')}
          disabled={row.type === ETranscoderProfileType.shared}
          onChangeHandler={onCodecChangeHandler}
        />
      ),
      sortable: true,
      minWidth: '150px',
    },
    // hide fps until will be avaialable in backend
    // {
    //   name: ColumnName(t, 'fps'),
    //   selector: row => row.fps,
    //   cell: row => (
    //     <CellSelectBox
    //       fieldName="fps"
    //       row={row}
    //       value={row?.fps?.toString() ?? ''}
    //       items={FpsOptions}
    //       placeholder={t('transcoder.forms.profile.video.fields.fps.placeholder')}
    //       disabled={row.type === EProfileType.shared}
    //       onChangeHandler={(event: SelectChangeEvent, row: Profile) =>
    //         onSelectChangeHandler({ event, row, field: 'fps' })
    //       }
    //     />
    //   ),
    //   sortable: true,
    //   minWidth: '140px',
    // },

    {
      name: ColumnName(t, 'resolution'),
      selector: row => row.resolution,
      cell: row => (
        <CellSelectBox
          fieldName="resolution"
          row={row}
          value={row?.resolution?.toString() ?? ''}
          items={Resolutions}
          placeholder={t('transcoder.forms.profile.video.fields.resolution.placeholder')}
          disabled={row.type === ETranscoderProfileType.shared}
          onChangeHandler={onResolutionChangeHandler}
        />
      ),

      sortable: true,
      width: '180px',
    },
    {
      name: ColumnName(t, 'bitrate', 'Mbps'),
      selector: row => row.bitrate,
      cell: row => (
        <CellInput
          name="bitrate"
          defaultValue={row.bitrate ?? ''}
          error={!!row.errorMsg?.bitrate}
          helperText={row.errorMsg?.bitrate}
          placeholder={t('transcoder.forms.profile.video.fields.bitrate.placeholder')}
          disabled={row.type === ETranscoderProfileType.shared}
          row={row}
          onChangeCellHandler={onChangeHandler}
          {...positiveNumber()}
        />
      ),
      sortable: true,
      width: '140px',
    },
    {
      name: ColumnName(t, 'profile'),
      selector: row => row.profile,
      cell: row => (
        <CellSelectBox
          fieldName="profile"
          row={row}
          value={row?.profile?.toString() ?? ''}
          items={Profiles(row.codec)}
          placeholder={t('transcoder.forms.profile.video.fields.profile.placeholder')}
          disabled={row.type === ETranscoderProfileType.shared}
          onChangeHandler={(event: SelectChangeEvent<string>, row: TranscoderProfile) =>
            onSelectChangeHandler({ event, row, field: 'profile' })
          }
        />
      ),
      sortable: true,
    },
    {
      name: ColumnName(t, 'gopsize'),
      selector: row => row.gopSize,
      cell: row => (
        <CellInput
          name="gopSize"
          row={row}
          defaultValue={row.gopSize ?? ''}
          error={!!row.errorMsg?.gopSize}
          placeholder={t('transcoder.forms.profile.video.fields.gopsize.placeholder')}
          disabled={row.type === ETranscoderProfileType.shared}
          onChangeCellHandler={onChangeHandler}
          {...unsignedInteger()}
        />
      ),
    },
    ...actions,
  ];
};

export const getActions = (
  onDeleteHandler: (videoProfile: TranscoderVideoProfile) => void,
  t: TFunction,
): TableColumn<TranscoderVideoProfile>[] => [
  {
    cell: row => (
      <Tooltip title={t('transcoder.profile.action.delete')}>
        <Button
          key={`delete-${row.id}`}
          variant="contained"
          size="small"
          color="error"
          onClick={() => onDeleteHandler(row)}
          sx={{ padding: '4px', minWidth: '32px', marginLeft: '25px' }}
        >
          <DeleteIcon fontSize="small" />
        </Button>
      </Tooltip>
    ),
    sortable: false,
    width: '100px',
  },
];
